/**
 * Panneau simulation travaux V2 — tableau de synthèse interactif.
 *
 * Remplace (via ScenariosPanelGate) la V1 quand l'admin active la variante
 * `scenarios_panel_variant = "v2"`. Sections :
 *
 *   1. Recommandations — 4 combinaisons optimales (optimiseur greedy
 *      backend, un scénario par objectif : ROI / gain / budget / climat).
 *      « Charger » place la sélection dans les cartes.
 *   2. Cartes gestes — toute amélioration applicable au logement, avec
 *      coche, gains, coût ± aides, ROI. Conflits grisés dynamiquement
 *      (applicateurs exclusifs). Détail dépliable par geste.
 *   3. Barre de sélection sticky — totaux issus du custom-preview
 *      (recalcul combiné moteur : les gains ne sont JAMAIS sommés).
 *   4. Mes scénarios — sans limite, segmentés par famille dominante,
 *      accordéon compact.
 *   5. Matrice impact × coût — SVG, bulles = gestes, clic = sélection.
 */

import { useEffect, useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  createScenario,
  customPreviewScenario,
  deleteScenario,
  getApplicableImprovements,
  getOptimalScenarios,
  listScenarios,
  recalculateScenario,
  FAMILY_LABELS_DEFAULT,
  type CustomPreviewResponse,
  type ImprovementApplicability,
  type ImprovementBreakdown,
  type OptimalObjective,
  type Scenario,
} from "@/api/scenarios";
import { getCatalogs, type ImprovementOption } from "@/api/catalogs";
import { ApiError } from "@/api/client";
import { AmortizationChart } from "@/components/dpe/AmortizationChart";
import { EnergyLabel } from "@/components/dpe/EnergyLabel";

export interface ScenariosPanelV2Props {
  logementId: string;
  currentLabel: string;
}

// Icônes par famille de travaux.
const FAMILY_ICONS: Record<string, string> = {
  isolation: "🏠",
  vitrage: "🪟",
  chauffage: "🔥",
  ecs: "💧",
  ventilation: "💨",
  enr: "☀️",
  confort_ete: "🌞",
  divers: "📋",
  mixte: "🧩",
};

function familyIcon(family: string | undefined): string {
  return FAMILY_ICONS[family ?? "divers"] ?? "🔧";
}

function familyLabel(family: string | undefined): string {
  return FAMILY_LABELS_DEFAULT[family ?? "divers"] ?? family ?? "Autre";
}

const eur = (n: number | null | undefined): string =>
  n == null ? "—" : `${Math.round(n).toLocaleString("fr-FR")} €`;

export function ScenariosPanelV2({ logementId, currentLabel }: ScenariosPanelV2Props) {
  const qc = useQueryClient();
  // Sélection brute (réactive) puis débounced (drive la preview moteur).
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [previewKeys, setPreviewKeys] = useState<string[]>([]);
  const [detailKey, setDetailKey] = useState<string | null>(null);
  const [segFilter, setSegFilter] = useState<string>("all");
  const [savingName, setSavingName] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const { data: catalogs } = useQuery({
    queryKey: ["catalogs"],
    queryFn: getCatalogs,
    staleTime: 10 * 60 * 1000,
  });
  const { data: applicable } = useQuery({
    queryKey: ["applicable-improvements", logementId],
    queryFn: () => getApplicableImprovements(logementId),
    staleTime: 5 * 60 * 1000,
  });
  const { data: optimal, isFetching: optimalLoading } = useQuery({
    queryKey: ["optimal-scenarios", logementId],
    queryFn: () => getOptimalScenarios(logementId),
    staleTime: 5 * 60 * 1000,
  });
  const { data: scenarios } = useQuery({
    queryKey: ["scenarios", logementId],
    queryFn: () => listScenarios(logementId),
  });

  // Debounce la sélection → preview combiné (recalcul moteur, non persisté).
  useEffect(() => {
    const keys = Array.from(selected).sort();
    const timer = setTimeout(() => setPreviewKeys(keys), 600);
    return () => clearTimeout(timer);
  }, [selected]);

  const previewQuery = useQuery({
    queryKey: ["custom-preview", logementId, previewKeys],
    queryFn: () => customPreviewScenario(logementId, previewKeys),
    enabled: previewKeys.length > 0,
    staleTime: 5 * 60 * 1000,
  });

  const savingsMut = useMutation({
    mutationFn: (name: string) =>
      createScenario(logementId, { name, improvements: Array.from(selected).sort() }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["scenarios", logementId] });
      setSavingName(null);
      setError(null);
    },
    onError: (err: unknown) => {
      if (err instanceof ApiError && err.code === "SCENARIO_DUPLICATE") {
        setError("Un scénario identique existe déjà — modifiez le nom.");
      } else if (err instanceof ApiError) {
        setError(err.message);
      } else {
        setError("Erreur lors de l'enregistrement.");
      }
    },
  });

  const deleteMut = useMutation({
    mutationFn: (id: string) => deleteScenario(logementId, id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["scenarios", logementId] }),
  });

  const recalcMut = useMutation({
    mutationFn: (id: string) => recalculateScenario(logementId, id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["scenarios", logementId] }),
  });

  // Conflicts : un geste bloque les gestes partageant son applicateur exclusif.
  const improvementsByApplicator = useMemo(() => {
    const map = new Map<string, string[]>(); // applicator → keys (exclusifs seulement)
    const exclusive = new Set(catalogs?.exclusive_applicators ?? []);
    for (const imp of catalogs?.improvements ?? []) {
      const app = imp.applicator;
      if (app && exclusive.has(app)) {
        map.set(app, [...(map.get(app) ?? []), imp.key]);
      }
    }
    return map;
  }, [catalogs]);

  // Clés bloquées par la sélection courante.
  const blockedKeys = useMemo(() => {
    const blocked = new Set<string>();
    for (const key of selected) {
      const imp = catalogs?.improvements.find((i) => i.key === key);
      if (!imp?.applicator) continue;
      for (const other of improvementsByApplicator.get(imp.applicator) ?? []) {
        if (other !== key) blocked.add(other);
      }
    }
    return blocked;
  }, [selected, catalogs, improvementsByApplicator]);

  const toggleGesture = (key: string, scrollToCard = false) => {
    setError(null);
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
      } else {
        if (blockedKeys.has(key)) return next; // conflict grisé — inerte
        next.add(key);
      }
      return next;
    });
    if (scrollToCard) {
      // Le clic vient de la matrice (hors viewport) : amène la carte au
      // centre de l'écran, au-dessus de la barre sticky, pour qu'on VOIT
      // le geste basculer au lieu d'une page figée.
      requestAnimationFrame(() => {
        document
          .querySelector(`[data-gesture-key="${key}"]`)
          ?.scrollIntoView({ behavior: "smooth", block: "center" });
      });
    }
  };

  const loadRecommended = (keys: string[]) => {
    setError(null);
    const next = new Set<string>();
    for (const key of keys) {
      if (!blockedKeys.has(key)) next.add(key);
    }
    setSelected(next);
    document
      .getElementById("v2-gesture-cards")
      ?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const baseLabel = applicable?.base_label ?? currentLabel;
  const preview = previewKeys.length > 0 ? previewQuery.data : undefined;

  return (
    <div className="space-y-6">
      {error && (
        <p role="alert" className="rounded border border-red-200 bg-red-50 p-3 text-sm text-red-800">
          {error}
        </p>
      )}
      <RecommendationsSection
        optimal={optimal?.items ?? {}}
        loading={optimalLoading}
        onLoad={loadRecommended}
      />
      <GestureCards
        baseLabel={baseLabel}
        catalogsImprovements={catalogs?.improvements ?? []}
        applicableItems={applicable?.items ?? {}}
        selected={selected}
        blockedKeys={blockedKeys}
        detailKey={detailKey}
        onToggle={toggleGesture}
        onDetail={setDetailKey}
      />
      <SelectionBar
        savingName={savingName}
        setSavingName={setSavingName}
        preview={preview}
        previewLoading={previewQuery.isFetching}
        previewError={
          previewQuery.isError && previewKeys.length > 0
            ? "Le recalcul du bouquet a échoué — réessayez ou retirez un geste."
            : null
        }
        selectedCount={selected.size}
        onSave={savingName ? () => savingsMut.mutate(savingName) : null}
        saving={savingsMut.isPending}
        onReset={() => {
          setSelected(new Set());
          setSavingName(null);
        }}
      />
      <SavedScenarios
        scenarios={scenarios ?? []}
        segFilter={segFilter}
        setSegFilter={setSegFilter}
        onDelete={(id) => deleteMut.mutate(id)}
        onRecalculate={(id) => recalcMut.mutate(id)}
        deleting={deleteMut.isPending}
        recalculating={recalcMut.isPending}
      />
      <MatrixSection
        baseLabel={baseLabel}
        catalogsImprovements={catalogs?.improvements ?? []}
        applicableItems={applicable?.items ?? {}}
        selected={selected}
        blockedKeys={blockedKeys}
        onToggle={(key) => toggleGesture(key, true)}
        optimal={optimal?.items}
      />
    </div>
  );
}

// ══════════════════════════ Recommandations ══════════════════════════════

/**
 * Progression estimée pendant l'optimisation moteur (~200 évaluations
 * backend, 3-6 s selon la charge). Le backend ne streamed pas de vraie
 * progression : on anime une courbe asymptotique plafonnée à 95 % qui
 * saute à 100 % quand la réponse arrive — l'utilisateur visualise que la
 * page vit, et les phases Miroir de l'ordre réel des objectifs.
 */
const OPTIMIZER_PHASES = [
  { upTo: 30, label: "ROI optimal" },
  { upTo: 55, label: "Gain énergétique max" },
  { upTo: 80, label: "Budget optimal" },
  { upTo: 96, label: "Décarbonation max" },
];

function useOptimizerProgress(active: boolean): number {
  const [pct, setPct] = useState(0);
  useEffect(() => {
    if (!active) return;
    const t0 = Date.now();
    const tick = () => {
      const t = (Date.now() - t0) / 1000;
      // Asymptotique : rapide au début, ralentit, plafonne à 96 % —
      // 100 % n'est affiché que par l'arrivée réelle du résultat.
      const estimate = 96 * (1 - Math.exp(-t / 3.2));
      setPct(Math.round(Math.min(96, estimate)));
    };
    tick();
    const id = setInterval(tick, 120);
    return () => clearInterval(id);
  }, [active]);
  return pct;
}

function OptimizerProgress({ pct }: { pct: number }) {
  const phase =
    OPTIMIZER_PHASES.find((ph) => pct <= ph.upTo)?.label ??
    OPTIMIZER_PHASES[OPTIMIZER_PHASES.length - 1].label;
  return (
    <div
      className="w-full max-w-sm rounded-lg border border-blue-100 bg-blue-50/70 px-3 py-2"
      role="status"
      aria-live="polite"
    >
      <div className="flex items-center justify-between text-xs font-medium text-blue-900">
        <span aria-hidden="true" className="mr-2 inline-block animate-spin">
          ⚡
        </span>
        <span className="flex-1">Optimisation moteur — {phase}…</span>
        <span className="tabular-nums">{pct}%</span>
      </div>
      <div className="mt-1.5 h-1.5 w-full overflow-hidden rounded-full bg-blue-100">
        <div
          className="h-full rounded-full bg-gradient-to-r from-emerald-500 to-blue-500 transition-all duration-150"
          style={{ width: `${pct}%` }}
        />
      </div>
      <p className="mt-0.5 text-[10px] text-blue-700/70">
        ~200 combinaisons testées par le moteur (≈ 4-6 s)
      </p>
    </div>
  );
}

function RecommendationsSection({
  optimal,
  loading,
  onLoad,
}: {
  optimal: Partial<Record<OptimalObjective, { keys: string[]; label_before: string; label_after: string; final_kwh_saved: number; net_cost_eur: number; aids_total_eur: number; roi_years: number | null; ges_saved: number; score_note: string; icon: string; objective_label: string }>>;
  loading: boolean;
  onLoad: (keys: string[]) => void;
}) {
  const pct = useOptimizerProgress(loading);
  const entries = Object.entries(optimal);
  return (
    <section aria-label="Scénarios recommandés">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <h3 className="text-sm font-semibold uppercase tracking-wide text-gray-700">
            🏆 Scénarios recommandés
          </h3>
          <p className="text-xs text-gray-500">
            Combinaison optimale calculée sur votre logement — un scénario par objectif
          </p>
        </div>
        {loading && <OptimizerProgress pct={pct} />}
      </div>
      <div className="mt-3 grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
        {entries.map(([objective, opt]) => (
          <RecommendationCard
            key={objective}
            opt={opt}
            highlighted={objective === "roi"}
            onLoad={onLoad}
          />
        ))}
      </div>
    </section>
  );
}

function RecommendationCard({
  opt,
  highlighted,
  onLoad,
}: {
  opt: {
    keys: string[];
    label_before: string;
    label_after: string;
    final_kwh_saved: number;
    net_cost_eur: number;
    aids_total_eur: number;
    roi_years: number | null;
    ges_saved: number;
    score_note: string;
    icon: string;
    objective_label: string;
  };
  highlighted: boolean;
  onLoad: (keys: string[]) => void;
}) {
  return (
    <div
      className={`rounded-xl border bg-white p-4 shadow-sm ${
        highlighted ? "border-emerald-400 shadow-emerald-100" : "border-gray-200"
      }`}
    >
      <div className="flex items-center justify-between">
        <span aria-hidden="true">{opt.icon}</span>
        {highlighted && (
          <span className="rounded-full bg-emerald-100 px-2 py-0.5 text-xs font-semibold text-emerald-800">
            Recommandé
          </span>
        )}
      </div>
      <h4 className="mt-1 font-semibold text-gray-900">{opt.objective_label}</h4>
      <div className="mt-2 flex items-center gap-1.5">
        <EnergyLabel label={opt.label_before} size="sm" />
        <span aria-hidden="true" className="text-gray-400">→</span>
        <EnergyLabel label={opt.label_after} size="sm" />
      </div>
      <dl className="mt-2 space-y-1 text-xs text-gray-600">
        <div className="flex justify-between"><dt>kWh évités</dt><dd className="font-semibold text-emerald-700 tabular-nums">−{Math.round(opt.final_kwh_saved)}</dd></div>
        <div className="flex justify-between"><dt>Reste à charge</dt><dd className="font-semibold tabular-nums">{eur(opt.net_cost_eur)}</dd></div>
        <div className="flex justify-between"><dt>Aides</dt><dd className="tabular-nums text-emerald-700">{eur(opt.aids_total_eur)}</dd></div>
        <div className="flex justify-between"><dt>ROI</dt><dd className="font-semibold tabular-nums">{opt.roi_years != null ? `${opt.roi_years} ans` : "—"}</dd></div>
        <div className="flex justify-between"><dt>CO₂ évité</dt><dd className="tabular-nums">−{opt.ges_saved.toFixed(1)} kg/m²/an</dd></div>
      </dl>
      <p className="mt-1 text-[11px] text-gray-400">{opt.score_note}</p>
      <button
        type="button"
        onClick={() => onLoad(opt.keys)}
        className={`mt-3 w-full rounded-lg px-3 py-2 text-sm font-semibold text-white transition hover:opacity-90 ${
          highlighted ? "bg-emerald-700" : "bg-blue-900"
        }`}
      >
        Charger ce scénario
      </button>
    </div>
  );
}

// ══════════════════════════ Cartes gestes ════════════════════════════════

function GestureCards({
  baseLabel,
  catalogsImprovements,
  applicableItems,
  selected,
  blockedKeys,
  detailKey,
  onToggle,
  onDetail,
}: {
  baseLabel: string;
  catalogsImprovements: ImprovementOption[];
  applicableItems: Record<string, ImprovementApplicability>;
  selected: Set<string>;
  blockedKeys: Set<string>;
  detailKey: string | null;
  onToggle: (key: string) => void;
  onDetail: (key: string | null) => void;
}) {
  const rows = catalogsImprovements.filter((imp) => {
    const it = applicableItems[imp.key];
    if (!it) return false;
    return it.applicable || (it.cost_max_eur ?? 0) > 0; // gestes hors-3CL affichés à part
  });
  const applicableRows = rows.filter((r) => applicableItems[r.key]?.applicable);
  const nonApplicableRows = rows.filter((r) => !applicableItems[r.key]?.applicable);

  // Segmentation par famille (isolation, chauffage…) — lisibilité du scan.
  const FAM_ORDER = [
    "isolation", "vitrage", "chauffage", "ecs",
    "ventilation", "enr", "confort_ete", "divers",
  ];
  const grouped = new Map<string, ImprovementOption[]>();
  for (const imp of applicableRows) {
    const fam = imp.family ?? "divers";
    grouped.set(fam, [...(grouped.get(fam) ?? []), imp]);
  }
  const familyGroups = [...grouped.entries()].sort(
    (a, b) =>
      FAM_ORDER.indexOf(a[0]) - FAM_ORDER.indexOf(b[0]) || a[0].localeCompare(b[0]),
  );

  return (
    <section id="v2-gesture-cards" aria-label="Toutes les améliorations">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h3 className="text-sm font-semibold uppercase tracking-wide text-gray-700">
          Toutes les améliorations pour ce logement
        </h3>
        <p className="text-xs text-gray-500">
          {applicableRows.length} gestes pertinents · {nonApplicableRows.length} déjà optimaux · étiquette actuelle {baseLabel}
        </p>
      </div>
      {familyGroups.map(([fam, list]) => (
        <div key={fam} className="mt-5 first:mt-3">
          <div className="flex items-center gap-2" aria-hidden="true">
            <span className="text-lg">{familyIcon(fam)}</span>
            <h4 className="text-xs font-bold uppercase tracking-wider text-gray-500">
              {familyLabel(fam)}
            </h4>
            <span className="text-[11px] text-gray-400">· {list.length}</span>
            <span className="h-px flex-1 bg-gray-100" />
          </div>
          <div className="mt-3 grid gap-4 md:grid-cols-2 xl:grid-cols-3">
            {list.map((imp) => {
              const it = applicableItems[imp.key];
              const isBlocked = blockedKeys.has(imp.key) && !selected.has(imp.key);
              const isSel = selected.has(imp.key);
              return (
                <GestureCard
                  key={imp.key}
                  imp={imp}
                  info={it}
                  selected={isSel}
                  blocked={isBlocked}
                  expanded={detailKey === imp.key}
                  onToggle={() => onToggle(imp.key)}
                  onDetail={() => onDetail(detailKey === imp.key ? null : imp.key)}
                />
              );
            })}
          </div>
        </div>
      ))}
      {nonApplicableRows.length > 0 && (
        <details className="mt-4 rounded border border-gray-200 bg-gray-50 p-3">
          <summary className="cursor-pointer text-xs font-semibold text-gray-600">
            {nonApplicableRows.length} gestes déjà optimaux ou non modélisés (voir)
          </summary>
          <ul className="mt-2 list-disc pl-5 text-xs text-gray-500">
            {nonApplicableRows.map((r) => (
              <li key={r.key}>{r.label ?? r.key} — aucun gain mesurable sur ce logement</li>
            ))}
          </ul>
        </details>
      )}
    </section>
  );
}
function GestureCard({
  imp,
  info,
  selected,
  blocked,
  expanded,
  onToggle,
  onDetail,
}: {
  imp: ImprovementOption;
  info: ImprovementApplicability;
  selected: boolean;
  blocked: boolean;
  expanded: boolean;
  onToggle: () => void;
  onDetail: () => void;
}) {
  const kwh = info.kwh_saved ?? 0;
  const roi = info.roi_years ?? null;
  const roiColor = roi == null ? "text-gray-400" : roi < 10 ? "text-emerald-700" : roi <= 20 ? "text-emerald-700" : "text-orange-700";
  return (
    <div
      role="checkbox"
      aria-checked={selected}
      data-gesture-key={imp.key}
      style={{ scrollMarginBottom: "7rem" }}
      tabIndex={blocked ? -1 : 0}
      onKeyDown={(e) => {
        if ((e.key === "Enter" || e.key === " ") && !blocked) {
          e.preventDefault();
          onToggle();
        }
      }}
      onClick={() => !blocked && onToggle()}
      className={`cursor-pointer rounded-xl border p-4 shadow-sm transition ${
        blocked
          ? "cursor-not-allowed opacity-50 grayscale"
          : "hover:shadow-md"
      } ${
        selected
          ? "border-emerald-400 bg-emerald-50/60 ring-2 ring-emerald-200"
          : "border-gray-200 bg-white"
      }`}
    >
      <div className="flex items-start gap-2">
        <span
          aria-hidden="true"
          className={`mt-0.5 flex h-6 w-6 flex-none items-center justify-center rounded-full border-2 ${
            selected ? "border-emerald-500 bg-emerald-500" : "border-gray-300"
          }`}
        >
          {selected && (
            <svg width="12" height="12" viewBox="0 0 14 14" fill="none">
              <path d="M2 7.5 5.5 11 12 3.5" stroke="#fff" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          )}
        </span>
        <div className="min-w-0 flex-1">
          <h4 className="font-semibold leading-tight text-gray-900">{imp.label ?? imp.key}</h4>
        </div>
      </div>
      {blocked && (
        <p className="mt-2 rounded bg-orange-50 p-2 text-xs text-orange-800">
          ⚠️ Exclusif avec un geste déjà coché (même poste) — décochez-le d'abord.
        </p>
      )}
      <div className="mt-3 grid grid-cols-3 gap-2 text-center text-xs">
        <div>
          <p className="font-bold text-emerald-700 tabular-nums">−{Math.round(kwh)}</p>
          <p className="text-[10px] uppercase tracking-wide text-gray-400">kWh/an</p>
        </div>
        <div>
          <p className="font-bold text-gray-900 tabular-nums">{eur(info.net_cost_eur)}</p>
          <p className="text-[10px] uppercase tracking-wide text-gray-400">net d'aides</p>
        </div>
        <div>
          <p className={`font-bold tabular-nums ${roiColor}`}>{roi != null ? `${roi} ans` : "—"}</p>
          <p className="text-[10px] uppercase tracking-wide text-gray-400">ROI</p>
        </div>
      </div>
      <div className="mt-2 flex items-center justify-between text-xs text-gray-500">
        <span className="tabular-nums">
          Coût {eur(info.cost_min_eur)} – {eur(info.cost_max_eur)} · aides {eur(info.aids_total_eur)}
        </span>
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); onDetail(); }}
          className="font-semibold text-blue-700"
          aria-expanded={expanded}
        >
          {expanded ? "▲ Détail" : "▼ Détail"}
        </button>
      </div>
      {expanded && (
        <div className="mt-2 border-t border-gray-100 pt-2 text-xs text-gray-600">
          <p>€/an : <strong className="tabular-nums">{eur(info.annual_eur_saved)}</strong></p>
          {info.label_after && info.label_after !== "?" && (
            <p className="mt-1 flex items-center gap-1">
              Étiquette atteinte : <EnergyLabel label={info.label_after} size="sm" />
            </p>
          )}
          {info.ges_per_m2_delta != null && info.ges_per_m2_delta > 0 && (
            <p className="mt-1">CO₂ évité : −{info.ges_per_m2_delta.toFixed(1)} kg/m²/an</p>
          )}
        </div>
      )}
    </div>
  );
}

// ══════════════════════════ Barre de sélection sticky ════════════════════

function SelectionBar({
  savingName,
  setSavingName,
  preview,
  previewLoading,
  previewError,
  selectedCount,
  onSave,
  saving,
  onReset,
}: {
  savingName: string | null;
  setSavingName: (v: string | null) => void;
  preview: CustomPreviewResponse | undefined;
  previewLoading: boolean;
  previewError?: string | null;
  selectedCount: number;
  onSave: (() => void) | null;
  saving: boolean;
  onReset: () => void;
}) {
  if (selectedCount === 0) return null;
  return (
    <div
      role="status"
      aria-live="polite"
      className="sticky bottom-4 z-20 rounded-xl border border-gray-200 bg-white/95 p-4 shadow-lg backdrop-blur"
    >
      {previewError && (
        <p className="mb-2 text-xs font-medium text-orange-700" role="alert">
          {previewError}
        </p>
      )}
      {previewLoading && !preview && (
        <p className="text-xs text-gray-500">⏳ Recalcul du bouquet par le moteur…</p>
      )}
      {preview && (
        <>
          <div className="flex flex-wrap items-center gap-x-5 gap-y-2 text-sm">
            <span className="text-xs font-semibold uppercase tracking-wide text-gray-400">
              Sélection
            </span>
            <span className="rounded-full bg-blue-50 px-2 py-0.5 text-xs font-semibold text-blue-900">
              {selectedCount} geste{selectedCount > 1 ? "s" : ""}
            </span>
            <span className="font-semibold text-emerald-700 tabular-nums">−{Math.round(preview.final_kwh_saved)} kWh/an</span>
            <span className="text-gray-700 tabular-nums">−{preview.annual_eur_saved.toLocaleString("fr-FR")} €/an</span>
            <span className="font-semibold tabular-nums">
              ROI {preview.roi_years != null ? `${preview.roi_years} ans` : "—"}
            </span>
            <span className="flex items-center gap-1">
              <EnergyLabel label={preview.label_before} size="sm" />
              <span className="text-gray-400">→</span>
              <EnergyLabel label={preview.label_after} size="sm" />
            </span>
          </div>
          {/* Barre empilée aides / reste à charge (ratio du coût total) */}
          {preview.net_cost_eur + preview.aids_total_eur > 0 && (
            <div className="mt-3">
              <div
                className="flex h-2.5 w-full overflow-hidden rounded-full bg-gray-100"
                role="img"
                aria-label={`Décomposition coût total : aides ${eur(preview.aids_total_eur)}, reste à charge ${eur(preview.net_cost_eur)}`}
              >
                <div
                  className="h-full bg-emerald-500"
                  style={{
                    width: `${Math.min(100, (preview.aids_total_eur / (preview.aids_total_eur + preview.net_cost_eur)) * 100)}%`,
                  }}
                />
                <div
                  className="h-full flex-1 bg-orange-400"
                />
              </div>
              <div className="mt-1 flex justify-between text-xs">
                <span className="font-semibold text-emerald-700 tabular-nums">
                  Aides : {eur(preview.aids_total_eur)}
                </span>
                <span className="font-semibold text-orange-700 tabular-nums">
                  Reste à charge : {eur(preview.net_cost_eur)}
                </span>
              </div>
              {preview.ampleur?.is_eligible && (
                <p className="mt-1 text-[11px] text-gray-500">
                  🎁 Parcours rénovation d'ampleur activé (aides maximisées) — Accompagnateur Rénov' inclus.
                </p>
              )}
            </div>
          )}
        </>
      )}
      {savingName != null ? (
        <div className="mt-3 flex gap-2">
          <input
            type="text"
            value={savingName}
            onChange={(e) => setSavingName(e.target.value)}
            placeholder="Nom du scénario (ex : Grenier + PAC)"
            className="flex-1 rounded border border-gray-300 px-3 py-2 text-sm"
            maxLength={120}
          />
          <button
            type="button"
            disabled={!savingName.trim() || saving || !onSave}
            onClick={onSave ?? undefined}
            className="rounded bg-emerald-700 px-4 py-2 text-sm font-semibold text-white disabled:opacity-50"
          >
            {saving ? "Enregistrement…" : "Valider"}
          </button>
          <button type="button" onClick={() => setSavingName(null)} className="text-sm text-gray-500">
            Annuler
          </button>
        </div>
      ) : (
        <div className="mt-3 flex flex-wrap gap-2 justify-end">
          <button
            type="button"
            onClick={onReset}
            className="rounded-lg border border-gray-300 px-3 py-2 text-sm font-medium text-gray-600 hover:bg-gray-50"
          >
            Réinitialiser
          </button>
          <button
            type="button"
            disabled={!preview}
            onClick={() => setSavingName("")}
            className="rounded-lg bg-blue-900 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-800 disabled:opacity-50"
          >
            Enregistrer ce scénario
          </button>
        </div>
      )}
    </div>
  );
}

// ══════════════════════════ Mes scénarios segmentés ══════════════════════

function SavedScenarios({
  scenarios,
  segFilter,
  setSegFilter,
  onDelete,
  onRecalculate,
  deleting,
  recalculating,
}: {
  scenarios: Scenario[];
  segFilter: string;
  setSegFilter: (v: string) => void;
  onDelete: (id: string) => void;
  onRecalculate: (id: string) => void;
  deleting: boolean;
  recalculating: boolean;
}) {
  if (scenarios.length === 0) return null;
  // Groupe par catégorie (famille dominante stockée / dérivée par le backend).
  const grouped = groupByCategory(scenarios);
  const segments = displaySegments(grouped);

  return (
    <section aria-label="Mes scénarios enregistrés">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <h3 className="text-sm font-semibold uppercase tracking-wide text-gray-700">
            Mes scénarios enregistrés
          </h3>
          <p className="text-xs text-gray-500">
            Sans limite · segmentés par lot de travaux · cliquez pour déplier
          </p>
        </div>
        <span className="rounded-full bg-gray-100 px-2 py-0.5 text-xs font-semibold text-gray-700">
          {scenarios.length} enregistré{scenarios.length > 1 ? "s" : ""}
        </span>
      </div>
      {segments.length > 1 && (
        <div className="mt-3 flex flex-wrap gap-2">
          <button
            type="button"
            onClick={() => setSegFilter("all")}
            className={segChipClass(segFilter === "all")}
          >
            Tout · {scenarios.length}
          </button>
          {segments.map(([cat, count]) => (
            <button
              key={cat}
              type="button"
              onClick={() => setSegFilter(cat)}
              className={segChipClass(segFilter === cat)}
            >
              {familyIcon(cat === "all" ? "mixte" : cat)} {familyLabel(cat === "all" ? "mixte" : cat)} · {count}
            </button>
          ))}
        </div>
      )}
      <ul className="mt-3 space-y-2">
        {scenarios
          .filter((s) => segFilter === "all" || (s as Scenario & { category?: string }).category === segFilter)
          .map((s) => (
            <SavedScenarioRow
              key={s.id}
              scenario={s}
              onDelete={() => onDelete(s.id)}
              onRecalculate={() => onRecalculate(s.id)}
              deleting={deleting}
              recalculating={recalculating}
            />
          ))}
      </ul>
    </section>
  );
}

function groupByCategory(scenarios: Scenario[]): Record<string, Scenario[]> {
  const groups: Record<string, Scenario[]> = {};
  for (const s of scenarios) {
    const cat = (s as Scenario & { category?: string }).category ?? "mixte";
    groups[cat] = [...(groups[cat] ?? []), s];
  }
  return groups;
}

function displaySegments(
  groups: Record<string, Scenario[]>,
): [string, number][] {
  return Object.entries(groups).map(
    ([cat, list]) => [cat, list.length] as [string, number],
  );
}

function segChipClass(active: boolean): string {
  return active
    ? "rounded-full bg-blue-900 px-3 py-1 text-xs font-semibold text-white"
    : "rounded-full bg-gray-100 px-3 py-1 text-xs font-semibold text-gray-700 hover:bg-gray-200";
}

/** Reste à charge net d'aides d'un scénario enregistré (coût moyen − aides). */
function netCostOf(s: Scenario): number {
  const avgCost =
    (Number(s.cost_estimate_min ?? 0) + Number(s.cost_estimate_max ?? 0)) / 2;
  return Math.max(0, avgCost - Number(s.estimated_aids?.total_eur ?? 0));
}

function SavedScenarioRow({
  scenario: s,
  onDelete,
  onRecalculate,
  deleting,
  recalculating,
}: {
  scenario: Scenario;
  onDelete: () => void;
  onRecalculate: () => void;
  deleting: boolean;
  recalculating: boolean;
}) {
  const [open, setOpen] = useState(false);
  const obsolete = s.engine_version && s.current_engine_version && s.engine_version !== s.current_engine_version;
  return (
    <li className="rounded-2xl border border-gray-200 bg-white p-3">
      <div
        className="flex cursor-pointer items-center gap-3"
        onClick={() => setOpen(!open)}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => { if (e.key === "Enter") setOpen(!open); }}
        aria-expanded={open}
      >
        <span className="flex items-center gap-1">
          <EnergyLabel label={s.label_before ?? "?"} size="sm" />
          <span className="text-gray-400">→</span>
          <EnergyLabel label={s.label_after ?? "?"} size="sm" />
        </span>
        <div className="min-w-0 flex-1">
          <p className="truncate text-sm font-semibold text-gray-900">{s.name}</p>
          <p className="text-[11px] text-gray-400">
            {familyIcon((s as Scenario & { category?: string }).category)}{" "}
            {familyLabel((s as Scenario & { category?: string }).category)} ·{" "}
            {s.improvements.length} geste{(s.improvements.length > 1) ? "s" : ""} ·{" "}
            {new Date(s.created_at).toLocaleDateString("fr-FR")}
          </p>
        </div>
        {s.roi_years != null && (
          <span
            className={`rounded-full px-2 py-0.5 text-xs font-semibold ${
              s.relevance === "excellent" || s.relevance === "bon"
                ? "bg-emerald-100 text-emerald-800"
                : s.relevance === "non_rentable" || s.relevance === "inadapte"
                  ? "bg-orange-100 text-orange-800"
                  : "bg-blue-100 text-blue-900"
            }`}
          >
            ROI {Number(s.roi_years)} ans
          </span>
        )}
        <span className="text-xs text-gray-400">{open ? "▴" : "▾"}</span>
      </div>
      {open && (
        <div className="mt-3 border-t border-gray-100 pt-3 text-xs text-gray-600">
          <p className="tabular-nums">
            −{Math.round(s.final_kwh_before ?? 0) - Math.round(s.final_kwh_after ?? 0) > 0
              ? `-${Math.round((s.final_kwh_before ?? 0) - (s.final_kwh_after ?? 0))} kWh/an`
              : "—"}{" "}
            · {eur(s.estimated_aids?.total_eur ?? null)} d'aides ·{" "}
            {eur(Number(s.cost_estimate_min))} – {eur(Number(s.cost_estimate_max))}
          </p>
          {s.relevance_message && <p className="mt-1 text-gray-500">{s.relevance_message}</p>}
          {s.dropped_improvements && s.dropped_improvements.length > 0 && (
            <p className="mt-1 rounded bg-blue-50 p-2 text-blue-900">
              ℹ️ {s.dropped_improvements.length} geste(s) ignoré(s) (conflit ou déjà optimal) :
              {s.dropped_improvements.map((k) => ` ${k}`).join(", ")}
            </p>
          )}
          {s.breakdown && s.breakdown.length > 0 && (
            <details className="mt-2">
              <summary className="cursor-pointer font-semibold">Bénéfice par amélioration</summary>
              <table className="mt-2 w-full">
                <thead className="text-left text-gray-400">
                  <tr>
                    <th className="py-1 font-medium">Geste</th>
                    <th className="py-1 text-right font-medium">kWh évités</th>
                    <th className="py-1 text-right font-medium">Coût</th>
                    <th className="py-1 text-right font-medium">Aides</th>
                  </tr>
                </thead>
                <tbody>
                  {s.breakdown.map((b: ImprovementBreakdown) => (
                    <tr key={b.key} className="border-t border-gray-100">
                      <td className="py-1">{b.label}</td>
                      <td className="py-1 text-right tabular-nums text-emerald-700">−{Math.round(b.final_kwh_saved)}</td>
                      <td className="py-1 text-right tabular-nums">{eur((b.cost_min_eur + b.cost_max_eur) / 2)}</td>
                      <td className="py-1 text-right tabular-nums">{eur(b.aids_eur)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </details>
          )}
          {s.annual_eur_saved != null && s.annual_eur_saved > 0 && (
            <details className="mt-2">
              <summary className="cursor-pointer font-semibold">
                Retour sur investissement
              </summary>
              <div className="mt-3">
                <AmortizationChart
                  netCostEur={netCostOf(s)}
                  annualSavingsEur={s.annual_eur_saved}
                  roiYears={s.roi_years ?? null}
                />
                <p className="mt-2 text-[11px] text-gray-400">
                  Reste à charge net d'aides : {eur(netCostOf(s))} · économies
                  attendues : {eur(s.annual_eur_saved)}/an · point breakeven :{" "}
                  {s.roi_years != null ? `${Number(s.roi_years)} ans` : "—"}
                </p>
              </div>
            </details>
          )}
          <div className="mt-3 flex gap-4">
            {obsolete && (
              <button type="button" onClick={onRecalculate} disabled={recalculating} className="font-semibold text-blue-700 disabled:opacity-50">
                {recalculating ? "Recalcul…" : "⚠️ Recalculer (moteur à jour)"}
              </button>
            )}
            <button type="button" onClick={() => navigator.clipboard?.writeText(s.improvements.join("+"))} className="font-semibold text-gray-500">
              Copier la liste des gestes
            </button>
            <button type="button" onClick={onDelete} disabled={deleting} className="font-semibold text-red-700 disabled:opacity-50">
              Supprimer
            </button>
          </div>
        </div>
      )}
    </li>
  );
}

// ══════════════════════════ Matrice impact × coût ════════════════════════

// Couleurs de famille (matrice) — bleu/indigo = enveloppe, orange/jaune =
// équipements, cyan = ventilation, vert = EnR, ambre = confort d'été.
const FAM_MATRIX_COLORS: Record<string, string> = {
  isolation: "#3b82f6",
  vitrage: "#6366f1",
  chauffage: "#f97316",
  ecs: "#eab308",
  ventilation: "#06b6d4",
  enr: "#10b981",
  confort_ete: "#f59e0b",
  divers: "#64748b",
  mixte: "#0ea5e9",
};

const MATRIX_W = 560;
const MATRIX_H = 330;

// Chips « illuminer » — abrégés des 4 scénarios recommandés.
const OPTIMAL_CHIP_DEFS: Record<
  OptimalObjective,
  { short: string; icon: string }
> = {
  roi: { short: "ROI", icon: "⚡" },
  gain: { short: "Gain", icon: "🔋" },
  budget: { short: "Budget", icon: "💶" },
  climat: { short: "Climat", icon: "🌍" },
};

function MatrixSection({
  baseLabel,
  catalogsImprovements,
  applicableItems,
  selected,
  blockedKeys,
  onToggle,
  optimal,
}: {
  baseLabel: string;
  catalogsImprovements: ImprovementOption[];
  applicableItems: Record<string, ImprovementApplicability>;
  selected: Set<string>;
  blockedKeys: Set<string>;
  onToggle: (key: string) => void;
  optimal?: Partial<Record<OptimalObjective, { keys: string[] }>>;
}) {
  const [highlightSet, setHighlightSet] = useState<OptimalObjective | null>(null);

  const points = catalogsImprovements
    .filter((imp) => {
      const it = applicableItems[imp.key];
      return it?.applicable && (it.net_cost_eur ?? 0) > 0;
    })
    .map((imp) => {
      const it = applicableItems[imp.key];
      return {
        key: imp.key,
        label: imp.label ?? imp.key,
        icon: familyIcon(imp.family),
        family: imp.family ?? "divers",
        net: it.net_cost_eur ?? 0,
        kwh: it.kwh_saved ?? 0,
        // CO₂ évité par m² (delta GES du geste) — pilote la TAILLE de bulle.
        ges: Math.max(0, it.ges_per_m2_delta ?? 0),
        roi: it.roi_years ?? null,
      };
    })
    .sort((a, b) => b.kwh - a.kwh);

  if (points.length < 3) {
    return (
      <section
        className="rounded-xl border border-gray-200 bg-white p-4"
        aria-label="Matrice impact par coût"
      >
        <h3 className="text-sm font-semibold uppercase tracking-wide text-gray-700">
          Carte des opportunités
        </h3>
        <p className="mt-2 text-xs text-gray-500">
          Pas assez de gestes pertinents sur ce logement pour tracer une carte
          (minimum 3). Consultez le tableau ci-dessus.
        </p>
      </section>
    );
  }

  const maxNet = Math.max(...points.map((p) => p.net)) * 1.1;
  const maxKwh = Math.max(...points.map((p) => p.kwh)) * 1.15;
  const PAD_L = 55, PAD_B = 34, PAD_T = 18;
  const W = MATRIX_W, H = MATRIX_H;
  const x = (net: number) => PAD_L + (net / maxNet) * (W - PAD_L - 20);
  const y = (kwh: number) => H - PAD_B - (kwh / maxKwh) * (H - PAD_B - PAD_T);
  // Taille de bulle = CO₂ évité (kg/m²/an) — 3 paliers discrets.
  const radius = (ges: number) => (ges > 5 ? 24 : ges >= 1 ? 18 : 13);

  // Droites iso-ROI : ROI = net / (kWh × prix 0,18 €/kWh) → kWh = net/(ROI×0,18).
  const isoRoiLine = (years: number) => {
    const endNet = Math.min(maxNet, (maxKwh * years * 0.18) as number);
    const endKwh = endNet / (years * 0.18);
    return {
      x1: x(0), y1: y(0),
      x2: x(endNet), y2: y(endKwh),
      labelX: x(endNet), labelY: y(endKwh) - 6,
      label: `ROI ${years} ans`,
    };
  };
  const iso10 = isoRoiLine(10);
  const iso20 = isoRoiLine(20);

  // Anti-chevauchement : décale à droite une bulle trop proche d'une placée.
  const placed: { cx: number; cy: number; r: number }[] = [];
  const positions = points.map((p) => {
    let cx = x(p.net);
    const cy = y(p.kwh);
    const r = radius(p.ges);
    for (const q of placed) {
      if (
        Math.abs(q.cx - cx) < (q.r + r) * 0.9 &&
        Math.abs(q.cy - cy) < (q.r + r) * 0.9
      ) {
        cx = Math.min(cx + r * 1.7, W - 24);
      }
    }
    const pos = { cx, cy, r };
    placed.push(pos);
    return pos;
  });

  const families = [...new Set(points.map((p) => p.family))];
  const highlightKeys = new Set(
    highlightSet ? (optimal?.[highlightSet]?.keys ?? []) : [],
  );
  const tooltip = (p: (typeof points)[number]) =>
    `${p.label}\nCoût net : ${eur(p.net)} · −${Math.round(p.kwh)} kWh/an · ROI ${p.roi != null ? `${p.roi} ans` : "—"}\nCO₂ évité : ${p.ges.toFixed(1)} kg/m²/an`;

  return (
    <section
      className="rounded-xl border border-gray-200 bg-white p-4"
      aria-label="Matrice impact par coût"
    >
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-sm font-semibold uppercase tracking-wide text-gray-700">
            Carte des opportunités
          </h3>
          <p className="text-xs text-gray-500 mt-1">
            Cliquez une bulle pour sélectionner le geste · taille = CO₂ évité ·
            au-dessus de la ligne « ROI 10 ans » = rentabilisé en moins de 10 ans
          </p>
        </div>
        <span className="rounded-full bg-blue-50 px-2 py-0.5 text-xs font-semibold text-blue-900">
          Étiquette actuelle {baseLabel}
        </span>
      </div>
      {optimal && Object.keys(optimal).length > 0 && (
        <div className="mt-3 flex flex-wrap items-center gap-2">
          <span className="text-[11px] font-semibold uppercase tracking-wider text-gray-400">
            Illuminer :
          </span>
          <button
            type="button"
            onClick={() => setHighlightSet(null)}
            className={segChipClass(highlightSet === null)}
          >
            Aucun
          </button>
          {(Object.entries(OPTIMAL_CHIP_DEFS) as [OptimalObjective, { short: string; icon: string }][]).map(
            ([key, def]) => {
              const set = optimal?.[key];
              if (!set) return null;
              return (
                <button
                  key={key}
                  type="button"
                  onClick={() => setHighlightSet(key)}
                  className={segChipClass(highlightSet === key)}
                  aria-pressed={highlightSet === key}
                >
                  {def.icon} {def.short}
                </button>
              );
            },
          )}
        </div>
      )}
      <div className="mt-3 w-full overflow-x-auto hidden md:block">
        <svg
          viewBox={`0 0 ${W} ${H}`}
          className="min-w-[520px] w-full"
          role="img"
          aria-label="Nuage de points : coût net en abscisse, kWh évités en ordonnée, une bulle par geste, lignes iso-ROI"
        >
          {/* Quadrillage + graduations */}
          <g stroke="#e2e8f0" strokeDasharray="4 4">
            <line x1={PAD_L} y1={y(maxKwh / 2)} x2={W - 20} y2={y(maxKwh / 2)} />
            <line x1={x(maxNet / 2)} y1={PAD_T} x2={x(maxNet / 2)} y2={H - PAD_B} />
          </g>
          <g fontSize="9" fill="#94a3b8">
            <text x={PAD_L} y={H - PAD_B + 12} textAnchor="middle">0</text>
            <text x={x(maxNet / 2)} y={H - PAD_B + 12} textAnchor="middle">
              {(maxNet / 2000).toLocaleString("fr-FR")} k€
            </text>
            <text x={x(maxNet)} y={H - PAD_B + 12} textAnchor="middle">
              {(maxNet / 1000).toLocaleString("fr-FR")} k€
            </text>
            <text x={PAD_L - 6} y={H - PAD_B + 4} textAnchor="end">0</text>
            <text x={PAD_L - 6} y={y(maxKwh / 2) + 3} textAnchor="end">
              {Math.round(maxKwh / 2000)}k
            </text>
            <text x={PAD_L - 6} y={y(maxKwh) + 3} textAnchor="end">
              {Math.round(maxKwh / 1000)}k
            </text>
          </g>
          {/* Axes */}
          <g stroke="#cbd5e1">
            <line x1={PAD_L} y1={H - PAD_B} x2={W - 20} y2={H - PAD_B} />
            <line x1={PAD_L} y1={PAD_T} x2={PAD_L} y2={H - PAD_B} />
          </g>
          <text x={(W + PAD_L) / 2} y={H - 6} textAnchor="middle" fontSize="11" fill="#94a3b8">
            Coût net après aides →
          </text>
          <text x={13} y={(H - PAD_B + PAD_T) / 2} textAnchor="middle" fontSize="11" fill="#94a3b8" transform={`rotate(-90 13 ${(H - PAD_B + PAD_T) / 2})`}>
            kWh évités/an →
          </text>
          {/* Droites iso-ROI */}
          <g>
            <line x1={iso20.x1} y1={iso20.y1} x2={iso20.x2} y2={iso20.y2} stroke="#f59e0b" strokeWidth="1.5" strokeDasharray="6 4" />
            <text x={iso20.labelX} y={iso20.labelY} textAnchor="middle" fontSize="9" fontWeight="600" fill="#d97706">
              {iso20.label}
            </text>
            <line x1={iso10.x1} y1={iso10.y1} x2={iso10.x2} y2={iso10.y2} stroke="#059669" strokeWidth="1.5" strokeDasharray="6 4" />
            <text x={iso10.labelX} y={iso10.labelY - 10} textAnchor="middle" fontSize="9" fontWeight="600" fill="#047857">
              {iso10.label}
            </text>
          </g>
          {highlightKeys.size > 0 && highlightSet && (
            <text x={W - 20} y={PAD_T + 8} textAnchor="end" fontSize="10" fontWeight="700" fill="#d97706">
              ◌ halo doré = scénario {OPTIMAL_CHIP_DEFS[highlightSet].short}
            </text>
          )}
          {points.map((p, i) => {
            const pos = positions[i];
            const sel = selected.has(p.key);
            const blocked = blockedKeys.has(p.key);
            const inHighlight = highlightKeys.has(p.key);
            const color = blocked ? "#94a3b8" : FAM_MATRIX_COLORS[p.family] ?? "#64748b";
            return (
              <g key={p.key}>
                {inHighlight && (
                  <circle cx={pos.cx} cy={pos.cy} r={pos.r + 5} fill="none" stroke="#d97706" strokeWidth="2.5" opacity="0.85" pointerEvents="none" />
                )}
                {sel && (
                  <circle cx={pos.cx} cy={pos.cy} r={pos.r + 3} fill="none" stroke="#065f46" strokeWidth="3" pointerEvents="none" />
                )}
                <circle
                  cx={pos.cx}
                  cy={pos.cy}
                  r={pos.r}
                  fill={color}
                  fillOpacity={blocked ? 0.3 : 0.75}
                  stroke={blocked ? "#94a3b8" : sel ? "#065f46" : "#e2e8f0"}
                  strokeWidth={sel ? 2 : 1.5}
                  strokeDasharray={blocked ? "4 3" : undefined}
                  style={{ cursor: blocked ? "not-allowed" : "pointer" }}
                  tabIndex={blocked ? -1 : 0}
                  role="button"
                  aria-label={`${p.label} — ${eur(p.net)} net, −${Math.round(p.kwh)} kWh/an, ROI ${p.roi != null ? `${p.roi} ans` : "—"}, CO₂ évité ${p.ges.toFixed(1)} kg/m²/an${blocked ? " (conflit)" : ""}`}
                  onClick={() => !blocked && onToggle(p.key)}
                  onKeyDown={(e) => {
                    if ((e.key === "Enter" || e.key === " ") && !blocked) { e.preventDefault(); onToggle(p.key); }
                  }}
                >
                  <title>{tooltip(p)}</title>
                </circle>
                <text
                  x={pos.cx}
                  y={pos.cy + 4}
                  textAnchor="middle"
                  fontSize="10"
                  fontWeight="700"
                  fill="#fff"
                  pointerEvents="none"
                >
                  {blocked ? "⚠" : p.icon}
                </text>
                {i < 3 && (
                  <text
                    x={pos.cx + pos.r + 5}
                    y={pos.cy + 3}
                    fontSize="9"
                    fontWeight="600"
                    fill="#475569"
                    pointerEvents="none"
                  >
                    {p.label.length > 24 ? `${p.label.slice(0, 22)}…` : p.label}
                  </text>
                )}
              </g>
            );
          })}
        </svg>
      </div>
      {/* Légende familles */}
      <div className="mt-2 flex flex-wrap gap-3 text-[10px] text-gray-500">
        {families.map((fam) => (
          <span key={fam} className="inline-flex items-center gap-1">
            <span
              aria-hidden="true"
              className="inline-block h-2.5 w-2.5 rounded-full"
              style={{ background: FAM_MATRIX_COLORS[fam] ?? "#64748b" }}
            />
            {familyLabel(fam)}
          </span>
        ))}
        <span className="inline-flex items-center gap-1">
          <span aria-hidden="true" className="inline-block h-2.5 w-2.5 rounded-full bg-gray-400 opacity-60" />
          Conflit (grisé)
        </span>
      </div>
      {/* Mobile : liste top-5 à la place du nuage (pas de scroll horizontal) */}
      <div className="mt-3 md:hidden">
        <p className="text-[11px] font-semibold uppercase tracking-wider text-gray-400">
          Top gestes (par gain)
        </p>
        <ul className="mt-2 space-y-1.5">
          {points.slice(0, 5).map((p) => (
            <li
              key={p.key}
              role="button"
              tabIndex={blockedKeys.has(p.key) ? -1 : 0}
              onClick={() => !blockedKeys.has(p.key) && onToggle(p.key)}
              onKeyDown={(e) => {
                if ((e.key === "Enter" || e.key === " ") && !blockedKeys.has(p.key)) {
                  e.preventDefault();
                  onToggle(p.key);
                }
              }}
              className={`flex items-center gap-2 rounded-lg border p-2 text-xs ${
                selected.has(p.key)
                  ? "border-emerald-400 bg-emerald-50/60"
                  : "border-gray-200 bg-white"
              }`}
            >
              <span
                aria-hidden="true"
                className="inline-block h-2.5 w-2.5 flex-none rounded-full"
                style={{ background: FAM_MATRIX_COLORS[p.family] ?? "#64748b" }}
              />
              <span className="min-w-0 flex-1 truncate">{p.label}</span>
              <span className="tabular-nums text-emerald-700">−{Math.round(p.kwh)}</span>
              <span className="tabular-nums text-gray-500">{eur(p.net)}</span>
              <span className="tabular-nums text-gray-400">
                {p.roi != null ? `${p.roi}a` : "—"}
              </span>
            </li>
          ))}
        </ul>
      </div>
    </section>
  );
}
